import { useState, useRef } from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField, { TextFieldVariants } from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

type AutocompleteButtonProps = {
  onSelected: (selection: string, event: any) => void;
  options: string[];
  variant?: TextFieldVariants;
  children: any;
  placeholderText?: string;
  onInput?: (state: boolean) => void;
  disabled?: boolean;
  onTextInput?: (text: string) => void;
  sx?: any;
  buttonSx?: any;
  seamless?: boolean;
  renderOption?: any;
};

export function AutocompleteButton({
  onSelected,
  options,
  variant,
  children,
  placeholderText,
  onInput,
  disabled,
  onTextInput,
  sx,
  buttonSx,
  seamless,
  renderOption,
}: AutocompleteButtonProps) {
  const [input, setInput] = useState<boolean>(false);
  const [seamlessInput, setSeamlessInput] = useState<string>("");
  const ref = useRef<any>();

  const changeInput = (state: boolean) => {
    setInput(state);
    if (onInput) {
      onInput(state);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSelected((e.target as any).value, e);
      changeInput(false);
    } else if (e.key === "Escape") {
      changeInput(false);
    } else if (e.key === "Tab") {
      ref.current = e;
    }
  };

  const handleChange = (e: React.SyntheticEvent, val: string | null) => {
    if (val !== null) {
      if (e.type === "click") {
        onSelected(val, e);
      } else if (
        e.type === "blur" &&
        ref.current &&
        ref.current.key === "Tab"
      ) {
        onSelected(val, ref.current);
      }
      changeInput(false);
    }
  };

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onTextInput) {
      onTextInput(e.target.value);
    }
  };

  const dropdownProps = {
    componentsProps: {
      popper: { style: { width: "fit-content" } },
      paper: {
        sx: {
          fontSize: "12px",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          "& .MuiAutocomplete-option": {
            fontSize: "12px",
            minHeight: "32px",
            px: 1,
          },
        },
      },
    },
  };

  if (seamless) {
    const pillSx = {
      borderRadius: "12px",
      border: "1px dashed var(--text-tertiary)",
      color: "var(--text-tertiary)",
      fontSize: "11px",
      height: "22px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    };
    const pillInputSx = {
      "& .MuiInputBase-root": {
        fontSize: "11px",
        borderRadius: "12px",
        px: 1,
      },
      "& .MuiInputBase-input:focus::placeholder": { opacity: 0 },
      "& .MuiOutlinedInput-notchedOutline": {
        borderStyle: "dashed",
        borderColor: "var(--text-tertiary)",
        top: 0,
      },
      "& .MuiOutlinedInput-notchedOutline legend": {
        width: "0 !important",
        height: "0 !important",
        padding: "0 !important",
        fontSize: 0,
        overflow: "hidden",
      },
      "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderStyle: "dashed",
        borderColor: "var(--text-tertiary)",
        borderWidth: "1px",
      },
    };
    const pillInputProps = {
      style: {
        padding: 0,
        lineHeight: "1",
        textAlign: "center" as const,
        color: "var(--text-tertiary)",
      },
    };

    return (
      <Box sx={{ position: "relative", display: "inline-block" }}>
        {/* Always in DOM — defines the layout size */}
        <Box
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={disabled ? undefined : () => changeInput(true)}
          onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " "))
              changeInput(true);
          }}
          sx={{
            ...pillSx,
            px: 1,
            background: "none",
            cursor: disabled ? "default" : "pointer",
            whiteSpace: "nowrap",
            userSelect: "none",
            visibility: input ? "hidden" : "visible",
            opacity: disabled ? 0.38 : 1,
            "&:hover": disabled
              ? {}
              : {
                  borderColor: "var(--text-secondary)",
                  color: "var(--text-secondary)",
                },
          }}
        >
          {children}
        </Box>
        {/* Absolutely overlays the button — inherits its exact dimensions */}
        {input && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              "& .MuiAutocomplete-root": { margin: 0 },
            }}
          >
            <Autocomplete
              freeSolo
              autoHighlight
              autoSelect
              clearOnEscape
              disableClearable
              open={seamlessInput.length > 0}
              disabled={disabled}
              onChange={(e, val) => {
                setSeamlessInput("");
                handleChange(e, val);
              }}
              inputValue={seamlessInput}
              onInputChange={(_, val) => {
                setSeamlessInput(val);
                if (onTextInput) onTextInput(val);
              }}
              options={options}
              size="small"
              sx={{ width: "100%" }}
              {...dropdownProps}
              renderOption={renderOption}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={pillInputSx}
                  size="small"
                  variant="outlined"
                  autoFocus
                  placeholder={placeholderText}
                  inputProps={{ ...params.inputProps, ...pillInputProps }}
                  InputProps={{
                    ...params.InputProps,
                    style: { height: "22px", padding: "3px 8px" },
                  }}
                  onBlur={() => {
                    setSeamlessInput("");
                    setTimeout(() => changeInput(false), 120);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onSelected((e.target as any).value, e);
                      setSeamlessInput("");
                      changeInput(false);
                    } else if (e.key === "Escape") {
                      setSeamlessInput("");
                      changeInput(false);
                    }
                  }}
                />
              )}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <>
      {!input && (
        <IconButton
          size="small"
          onClick={() => changeInput(true)}
          disabled={disabled}
          sx={buttonSx}
        >
          {children}
        </IconButton>
      )}
      {input && (
        <Box
          sx={{
            maxWidth: "120px",
            width: "100%",
            mr: 0.5,
            alignSelf: "center",
          }}
        >
          <Autocomplete
            freeSolo
            autoHighlight
            autoSelect
            clearOnEscape
            disableClearable
            onChange={handleChange}
            options={options}
            size="small"
            {...dropdownProps}
            renderOption={renderOption}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    fontSize: "11px",
                    borderRadius: "12px",
                    height: "20px",
                    px: 1,
                  },
                  "& .MuiInputBase-input": {
                    p: "0 !important",
                    fontSize: "11px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--text-tertiary)",
                    opacity: 0.4,
                    top: 0,
                  },
                  "& .MuiOutlinedInput-notchedOutline legend": {
                    width: "0 !important",
                    height: "0 !important",
                    padding: "0 !important",
                    fontSize: 0,
                    overflow: "hidden",
                  },
                  "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "var(--text-secondary)",
                      borderWidth: "1px",
                    },
                  ...(sx ?? {}),
                }}
                size="small"
                variant={variant ?? "outlined"}
                autoFocus
                placeholder={placeholderText}
                InputProps={{
                  ...params.InputProps,
                  style: { height: "20px", padding: "2px 8px" },
                }}
                onChange={handleTextInput}
                onBlur={() => setTimeout(() => changeInput(false), 120)}
                onKeyDown={handleKey}
              />
            )}
          />
        </Box>
      )}
    </>
  );
}
